import { useMemo } from "react";
import { addDays, compareIsoDate, todayIn } from "../../../shared/dates";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useCalendarEvents, useMembers, useTasks } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { formatShortTime, useNow } from "../../ui/time";
import { fmtDate, placeEvent } from "../calendar/calendarModel";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";

interface UpcomingConfig {
  days: number;
  includeTasks: boolean;
  skipToday: boolean;
}

/** Next events in date order, plus tasks due — a digest built from existing providers. */
function Upcoming({ config, size }: WidgetProps<UpcomingConfig>) {
  const { timeZone, settings } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const from = config.skipToday ? addDays(today, 1) : today;
  const end = addDays(today, config.days + 1);
  const cal = useCalendarEvents(today, end);
  const tasks = useTasks();
  const state = dataStateOf(cal, 20 * 60_000);

  const members = useMembers().data ?? [];
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const rows = useMemo(
    () =>
      (cal.data?.events ?? [])
        .filter((e) => e.status !== "cancelled")
        .map((e) => placeEvent({ ...e, color: (e.people[0] && byId.get(e.people[0])?.color) || e.color }, timeZone))
        .filter((p) => compareIsoDate(p.firstDate, from) >= 0 || (p.lastDate >= from && p.event.allDay))
        .filter((p) => p.event.allDay || p.endMs > now.getTime())
        .sort((a, b) => (a.firstDate === b.firstDate ? a.startMs - b.startMs : a.firstDate < b.firstDate ? -1 : 1)),
    [cal.data, timeZone, from, now, byId],
  );
  // Chores live on the chore chart; this only nudges about household to-dos.
  const dueTasks = config.includeTasks ? (tasks.data ?? []).filter((t) => t.kind !== "chore" && !t.completedAt && t.dueDate && compareIsoDate(t.dueDate, today) <= 0) : [];
  const fontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const max = Math.max(1, Math.floor((size.height - fontPx * 4.5 - (dueTasks.length ? fontPx * 2.6 : 0)) / (fontPx * 2.35)));

  const when = (first: string, startMs: number, allDay: boolean) => {
    const day = first === today ? "Today" : first === addDays(today, 1) ? "Tomorrow" : fmtDate(first, { weekday: "short", month: "short", day: "numeric" });
    return allDay ? day : `${day}, ${formatShortTime(new Date(startMs), timeZone, settings.clock24h)}`;
  };

  if (!cal.data && state === "loading") return <div className="widget-loading" />;

  return (
    <div className="upcoming">
      <WidgetHeader title="Coming up" meta={cal.data?.source === "sample" ? <span className="sample-tag">Sample events</span> : undefined} />
      {dueTasks.length > 0 && (
        <p className="upcoming-tasks">
          <Icon name="tasks" size="1rem" />
          {dueTasks.length} task{dueTasks.length > 1 ? "s" : ""} due: {dueTasks.slice(0, 3).map((t) => t.title).join(", ")}
          {dueTasks.length > 3 ? "…" : ""}
        </p>
      )}
      {!cal.data ? (
        <EmptyState icon="calendar" title="Calendar unavailable" />
      ) : rows.length === 0 ? (
        <p className="muted">Nothing coming up.</p>
      ) : (
        <ul className="up-list">
          {rows.slice(0, max).map((p) => (
            <li key={p.event.id} style={{ ["--ev" as string]: p.event.color ?? "var(--accent)" }}>
              <i className="up-dot" />
              <span className="up-title">{p.event.displayTitle}</span>
              <span className="up-when">{when(p.firstDate, p.startMs, p.event.allDay)}</span>
              <span className="up-people">
                {p.event.people.slice(0, 3).map((id) => {
                  const m = byId.get(id);
                  return m ? <Avatar key={id} member={m} size="1.45rem" /> : null;
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
      <StatusBadge state={state} updatedAt={cal.dataUpdatedAt} />
    </div>
  );
}

export const upcomingWidget: WidgetDefinition<UpcomingConfig> = {
  type: "upcoming",
  version: 1,
  name: "Coming up",
  description: "The next events on the calendar, plus tasks due.",
  icon: "list",
  category: "calendar",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 2, h: 2 },
  stackHeight: 320,
  defaults: { days: 7, includeTasks: true, skipToday: false },
  settings: [
    {
      key: "days",
      label: "Look ahead",
      type: "select",
      options: [
        { value: 1, label: "Today + tomorrow" },
        { value: 2, label: "3 days" },
        { value: 6, label: "1 week" },
        { value: 13, label: "2 weeks" },
      ],
    },
    { key: "skipToday", label: "Start from tomorrow", type: "toggle", help: "Useful beside a calendar that already shows today." },
    { key: "includeTasks", label: "Include tasks due", type: "toggle" },
  ],
  provider: "calendar",
  Component: Upcoming,
};
